import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid, GuideFooter } from "@/components/guide/GuideSections";
import { JsonLd } from "@/components/JsonLd";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "Moz DA vs Ahrefs DR", path: "/moz-da-vs-ahrefs-dr" },
];

const ROWS: { label: string; da: string; dr: string }[] = [
  { label: "Full name", da: "Domain Authority", dr: "Domain Rating" },
  { label: "Vendor", da: "Moz", dr: "Ahrefs" },
  { label: "Scale", da: "0–100, logarithmic", dr: "0–100, logarithmic" },
  { label: "What it models", da: "Predicted likelihood of ranking, trained against real SERPs", dr: "Relative strength of the backlink profile only" },
  { label: "Inputs", da: "Linking root domains, link quality, Spam Score, MozRank-style signals", dr: "Number and DR of unique linking domains, plus how many sites each of those links out to" },
  { label: "Refresh cadence", da: "Roughly monthly", dr: "Continuous, often daily" },
  { label: "Index size", da: "Smaller link index", dr: "One of the largest link indexes in the market" },
  { label: "Spam sensitivity", da: "Some — Spam Score feeds the model", dr: "Minimal — a link is a link" },
  { label: "Free checks", da: "10 Link Explorer queries a month, plus MozBar", dr: "Unlimited basic lookups in the Website Authority Checker" },
  { label: "Easiest to inflate", da: "Harder", dr: "Easier — bulk links from high-DR pages move it fast" },
  { label: "Best used for", da: "Comparing prospects against a SERP, client reporting where DA is the agreed language", dr: "Fast link-profile triage and outreach prospecting at volume" },
];

const WHEN = [
  {
    t: "Use Ahrefs DR when you are judging links",
    d: "Prospecting, outreach filtering and competitor link-gap work all live in the link graph, which is exactly what DR measures — with the biggest index behind it. It updates daily, so a link you earn on Monday can show by Thursday.",
  },
  {
    t: "Use Moz DA when the client already speaks DA",
    d: "Plenty of briefs and RFPs still say \"we want DA 40\". DA is trained against real search results rather than links alone, so it degrades less badly when a site is stuffed with irrelevant links. Fighting the vocabulary rarely helps.",
  },
  {
    t: "Use Semrush Authority Score when money is on the line",
    d: "For due diligence — buying a domain, vetting a guest-post seller, deciding whether to keep an agency — Authority Score is the one that factors organic traffic and spam signals, so it exposes profiles the other two flatter.",
  },
];

const REDFLAGS = [
  { t: "High DR, low DA", d: "Usually a heavily built link profile: lots of links, weak topical relevance. Common on domains sold as \"aged, DR 60\"." },
  { t: "High DA, low DR", d: "Often an older brand with fewer but genuinely strong links, or a site Ahrefs has crawled more thoroughly than Moz. Rarely a problem." },
  { t: "Either score high, organic traffic near zero", d: "The clearest sign of a purchased profile. Check Semrush traffic before you value any domain on its authority score." },
  { t: "Sudden double-digit jump", d: "Almost always an index refresh or a vendor model change, not something you did. Check whether competitors moved the same week." },
];

const FAQS = [
  {
    q: "What is the difference between Moz DA and Ahrefs DR?",
    a: "Moz Domain Authority predicts how likely a site is to rank, trained against real search results using link quality plus spam signals. Ahrefs Domain Rating measures only the strength of a site's backlink profile relative to every other domain in Ahrefs' index. DA answers \"will this rank?\"; DR answers \"how strong are its links?\".",
  },
  {
    q: "Is DA or DR more accurate?",
    a: "Neither is accurate in an absolute sense — both are third-party estimates and Google uses neither. DR is more reliable for judging a backlink profile because Ahrefs has the larger link index and updates daily. DA is more useful for comparing candidates in a specific SERP because it is modelled against real rankings.",
  },
  {
    q: "Why is my DR higher than my DA?",
    a: "DR counts links regardless of topical relevance, so a profile with many links from unrelated but strong sites scores well. DA weighs quality and spam signals and is trained on ranking outcomes, so it discounts links that do not correspond to real search performance. A large gap in that direction usually means link volume without relevance.",
  },
  {
    q: "What is a good DA or DR score?",
    a: "For a young site, DA or DR above 20 shows real traction. 20–40 is a growing site, 40–60 is solid, 60–80 is strong, and 80+ belongs to major publishers. Both scales are logarithmic, so climbing from 10 to 20 takes months while 40 to 50 can take years. Judge against the sites ranking for your keyword, not the absolute number.",
  },
  {
    q: "Does Google use Domain Authority or Domain Rating?",
    a: "No. Both are vendor metrics — Moz built DA, Ahrefs built DR — and Google has repeatedly said it has no equivalent site-wide authority score. They are benchmarking and diagnostic tools. Rankings are decided page by page on relevance, content quality, links and user signals.",
  },
  {
    q: "Should I track DA, DR or Semrush Authority Score?",
    a: "Track one as the headline and keep the others as context. Authority Score is the strictest because it blends links, organic traffic and spam signals, which makes it the best single number for due diligence. Whichever you pick, never compare a DA reading in one month against a DR reading the next — the models are unrelated.",
  },
];

export const Route = createFileRoute("/moz-da-vs-ahrefs-dr")({
  head: () =>
    buildSeo({
      title: "Moz DA vs Ahrefs DR: What Each Score Actually Measures",
      description:
        "A side-by-side comparison of Moz Domain Authority and Ahrefs Domain Rating — how each is calculated, how often they refresh, which to trust for link buying, and what a big gap between them means.",
      path: "/moz-da-vs-ahrefs-dr",
      ogEyebrow: "Comparison",
      keywords:
        "moz da vs ahrefs dr, domain authority vs domain rating, DA vs DR, ahrefs domain rating explained, moz domain authority explained, semrush authority score comparison",
    }),
  component: DaVsDrPage,
});

function DaVsDrPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section headingLevel={1}
        eyebrow="Comparison"
        title={<>Moz DA vs <span className="text-gradient">Ahrefs DR</span></>}
        subtitle="Moz Domain Authority predicts ranking likelihood using link quality and spam signals, trained against real search results. Ahrefs Domain Rating measures backlink strength alone, on the larger index, updated daily. They are different models on the same 0–100 curve, and neither is a Google metric."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "DA models ranking likelihood; DR models link-profile strength. They answer different questions.",
              "DR refreshes daily on a larger link index; DA refreshes roughly monthly.",
              "DR is easier to inflate with bulk links, which is why bought domains advertise DR, not DA.",
              "A big DR-over-DA gap usually means link volume without topical relevance.",
              "Google uses neither. Both are for benchmarking against the sites you actually compete with.",
            ]}
          />
          <ReviewedBy
            reviewedOn="2026-08-18"
            credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh"
          />
        </div>
      </Section>

      <Section eyebrow="Side by side" title="Domain Authority vs Domain Rating">
        <div className="-mt-4 overflow-x-auto rounded-3xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <caption className="sr-only">
              Comparison of Moz Domain Authority and Ahrefs Domain Rating across calculation,
              refresh rate, index size and best use.
            </caption>
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="p-5 font-display text-base font-normal text-muted-foreground">Attribute</th>
                <th scope="col" className="p-5 font-display text-base font-normal">Moz DA</th>
                <th scope="col" className="p-5 font-display text-base font-normal">Ahrefs DR</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} className="border-b border-border/60 last:border-0 align-top">
                  <th scope="row" className="p-5 text-left font-medium text-muted-foreground">{r.label}</th>
                  <td className="p-5 text-muted-foreground">{r.da}</td>
                  <td className="p-5 text-muted-foreground">{r.dr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Choosing" title="Which score to use for which job">
        <div className="grid md:grid-cols-3 gap-6 -mt-4">
          {WHEN.map((w) => (
            <div key={w.t} className="rounded-3xl border border-border bg-card p-7">
              <h3 className="font-display text-lg">{w.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{w.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Diagnostics" title="What a gap between the two scores tells you">
        <GuideCardGrid items={REDFLAGS} />
      </Section>

      <GuideFooter
        nextTitle="Related reading"
        links={[
          { label: "How to check domain authority", to: "/how-to-check-domain-authority" },
          { label: "How to improve domain authority", to: "/how-to-improve-domain-authority" },
          { label: "SEO tool stack", to: "/seo-tools" },
          { label: "SEO glossary", to: "/glossary" },
        ]}
        aiContext="authority metrics"
        aiQuestions={["measure-aeo-citations", "what-is-entity-seo", "ai-search-traffic-analytics", "core-web-vitals-2026"]}
        faqs={FAQS}
        faqIdPrefix="faq-da-vs-dr"
        ctaTitle={<>Not sure which number your <span className="text-gradient">link profile</span> deserves?</>}
      />

      <JsonLd
        data={faqSchema(FAQS)}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Website authority metrics",
          url: `${SITE.url}/moz-da-vs-ahrefs-dr`,
          hasDefinedTerm: [
            {
              "@type": "DefinedTerm",
              name: "Domain Authority (DA)",
              description:
                "Moz's 0–100 score predicting how likely a website is to rank in search results, based on link quality, linking root domains and spam signals, trained against real search results.",
            },
            {
              "@type": "DefinedTerm",
              name: "Domain Rating (DR)",
              description:
                "Ahrefs' 0–100 score measuring the relative strength of a website's backlink profile, based on the number and strength of unique linking domains. It does not consider traffic or content quality.",
            },
          ],
        }}
      />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
